import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { projectData } from '@/data/project';
import { SEO_TAGLINES } from '@/lib/projectConstants';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-navy-light text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="font-display text-2xl font-bold">
                Nahar <span className="text-gold-light">Chandi Valley</span>
              </span>
            </Link>
            <p className="text-primary-foreground/80 mb-4">
              {SEO_TAGLINES.short}. 
              Experience premium living in an integrated township environment.
            </p>
          </div>

          {/* Core Pages - Primary Navigation */}
          <nav aria-label="Core pages">
            <h3 className="font-semibold text-lg mb-4">Explore</h3>
            <ul className="space-y-2">
              {[
                { label: 'Home', href: '/' },
                { label: 'Pricing', href: '/price' },
                { label: 'Floor Plans', href: '/floor-plans' },
                { label: 'Location', href: '/location' },
                { label: 'Amenities', href: '/amenities' },
                { label: 'Gallery', href: '/gallery' },
                { label: 'Virtual Tour', href: '/virtual-tour' },
                { label: 'Neighbourhood', href: '/neighbourhood' },
                { label: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-gold-light transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gold-light flex-shrink-0 mt-0.5" aria-hidden="true" />
                <a href={`tel:${projectData.contact.phone}`} className="text-primary-foreground/80 hover:text-gold-light">
                  {projectData.contact.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold-light flex-shrink-0 mt-0.5" aria-hidden="true" />
                <a 
                  href={`mailto:${projectData.contact.email}`} 
                  className="text-primary-foreground/80 hover:text-gold-light text-sm"
                >
                  {projectData.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold-light flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-primary-foreground/80">
                  Sales Office<br />
                  Chandivali, Mumbai 400072, Maharashtra
                </span>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <nav aria-label="Legal links">
            <h3 className="font-semibold text-lg mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  Disclaimer
                </Link>
              </li>
              <li>
                <Link to="/rera" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  RERA Information
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/sitemap" className="text-primary-foreground/80 hover:text-gold-light transition-colors">
                  Site Map
                </Link>
              </li>
              <li>
                <a 
                  href="/docs/gold-assurance-terms-nahar-chandi-valley.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/80 hover:text-gold-light transition-colors"
                >
                  Gold Assurance Terms
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* MahaRERA Compliance Section */}
        <div className="border-t border-primary-foreground/10 pt-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <a 
              href="https://maharera.maharashtra.gov.in/project-details/P51800040161"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 bg-white p-2 rounded-lg hover:shadow-lg transition-shadow"
            >
              <img 
                src="/assets/maharera-qr-code.png" 
                alt="MahaRERA QR Code - Scan to verify project registration"
                width={96}
                height={96}
                className="w-24 h-24"
              />
            </a>
            <div className="text-center md:text-left">
              <p className="font-semibold text-base text-primary-foreground mb-1">MahaRERA Registration</p>
              <p className="text-sm text-primary-foreground/80 mb-1">
                Project registered under MahaRERA
              </p>
              <p className="text-sm font-mono text-gold-light mb-2">
                Registration No: PM1180002500059
              </p>
              <p className="text-xs text-primary-foreground/70">
                Scan the QR code to view official project details
              </p>
            </div>
          </div>
        </div>

        {/* Campaign Mention */}
        <div className="mb-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-gold-light" aria-hidden="true">🎁</span>
            <p className="text-sm text-gold-light font-medium">
              Makar Sankranti Special — 10g Gold Assured on Every Booking (Valid till 31 March 2026)
            </p>
          </div>
        </div>

        {/* Project Advisory Statement */}
        <div className="mb-4">
          <p className="text-sm text-primary-foreground/70 leading-relaxed mb-4">
            <strong>Project Guidance:</strong> Project guidance presented in alignment with 
            the developer's planning, approvals, and current release structure.
          </p>
        </div>

        {/* General Disclaimer */}
        <div className="mb-6">
          <p className="text-sm text-primary-foreground/70 leading-relaxed">
            <strong>Disclaimer:</strong> This website presents project information in alignment 
            with developer planning and publicly available approvals. It does not constitute a 
            contractual offer. Final terms are governed by official agreements. Images, renders, 
            and specifications are artistic impressions and indicative only. Prices are subject 
            to change without notice. Images are representational. 
            Prospective buyers are advised to verify all details independently before making any decisions.
          </p>
        </div>

        {/* Gold Assurance Disclaimer */}
        <div className="mb-8">
          <p className="text-sm text-primary-foreground/70 leading-relaxed">
            <strong>Gold Assurance Disclaimer:</strong> The assured gold benefit is applicable on successful registration of the agreement for sale. The value of the gold shall be calculated based on the prevailing market rate of 22-carat gold (10 grams) as published on <a href="https://www.goodreturns.in" target="_blank" rel="noopener noreferrer" className="text-gold-light hover:underline">www.goodreturns.in</a> on the date of registration of the property. The benefit may be provided either in the form of a physical gold coin or its equivalent monetary value, at the sole discretion of the promoter/advisory partner. This assurance is subject to fulfillment of booking and registration milestones and does not form part of the consideration value of the property. Offer valid till 31st March 2026.
          </p>
        </div>

        {/* SEO Misspelling Capture - Phase 28 */}
        <div className="mb-6 text-center">
          <p className="text-xs text-primary-foreground/70 leading-relaxed">
            Searches related to Nahar Amrit Shakti, Nahar Chandivali, Nahar Chandi Wali, 
            Chandivali 3 BHK, Powai near flats, and new under-construction residential projects 
            in Chandivali often lead users to this development. Also known as: Nahar Chandivalli, 
            Nahar Chandi Valley Chandivali, Nahar Powai new project.
          </p>
        </div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-primary-foreground/10">
          <p className="text-sm text-primary-foreground/70">
            © {currentYear} Nahar Chandi Valley. All rights reserved.
          </p>
          <p className="text-sm text-primary-foreground/70">
            Developed by Nahar Group
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
